export let token = null;

const CANONICAL_USER_ID_KEY = 'fundmore_canonical_user_id';
const USER_CACHE_KEY = 'fundmore_user';

const getStorage = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    return window.localStorage;
  } catch (error) {
    return undefined;
  }
};

export const setCanonicalUserId = (userId) => {
  const storage = getStorage();
  if (!storage || userId === undefined || userId === null) {
    return;
  }
  storage.setItem(CANONICAL_USER_ID_KEY, String(userId));
};

export const getCanonicalUserId = () => {
  const storage = getStorage();
  if (!storage) {
    return undefined;
  }
  const storedValue = storage.getItem(CANONICAL_USER_ID_KEY);
  return storedValue !== null ? storedValue : undefined;
};

export const clearCanonicalUserId = () => {
  const storage = getStorage();
  if (!storage) {
    return;
  }
  storage.removeItem(CANONICAL_USER_ID_KEY);
};

export const clearCachedUserMetadata = () => {
  const storage = getStorage();
  if (!storage) {
    return;
  }
  storage.removeItem(USER_CACHE_KEY);
  clearCanonicalUserId();
};

//decodes jwt token
export function parseJwt(token) {
  if (!token) return null;
  var base64Url = token.split('.')[1];
  var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  var jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );

  return JSON.parse(jsonPayload);
}

// Stytch-based auth uses HttpOnly cookies for backend sessions.
// We no longer manage JWT access tokens on the frontend. Instead,
// we cache minimal user metadata (from /auth/me) in localStorage so
// legacy helpers (TopBar, ChatModel, etc.) continue to function.

export const cacheUserMetadata = (user, authContext) => {
  const storage = getStorage();
  if (!storage || !user) {
    return;
  }
  try {
    const tenantContext =
      authContext?.tenant_context ||
      authContext?.tenantContext ||
      authContext?.tenant ||
      {};
    // Normalise the shape of the cached user so downstream code
    // can rely on stable field names (matching the old Auth0 shape).
    const normalized = {
      user_id: user.user_id || user.id || user.userId,
      user_email: user.user_email || user.email,
      first_name: user.first_name || user.firstName,
      last_name: user.last_name || user.lastName,
      pending_application_id:
        user.pending_application_id || user.pendingApplicationId,
      role: user.role || user.originalRole || user.original_role,
      originalRole: user.originalRole || user.original_role,
      primary_branch_id:
        user.primary_branch_id ||
        user.primaryBranchId ||
        user.branch_id ||
        user.branchId ||
        tenantContext.branch_id ||
        tenantContext.branchId,
      branch_id:
        user.branch_id ||
        user.branchId ||
        user.primary_branch_id ||
        user.primaryBranchId ||
        tenantContext.branch_id ||
        tenantContext.branchId,
      permissions:
        (authContext && authContext.permissions) ||
        user.permissions ||
        [],
    };

    // Stash any legacy token field if present for backwards-compatibility.
    if (user.token) {
      token = user.token;
    }

    storage.setItem(USER_CACHE_KEY, JSON.stringify(normalized));
    if (user.user_id !== undefined && user.user_id !== null) {
      setCanonicalUserId(user.user_id);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[auth] Failed to cache user metadata', error);
  }
};

//get stored user metadata from localStorage
export const CheckAuth = () => {
  const storage = getStorage();
  if (!storage) return undefined;
  try {
    const raw = storage.getItem(USER_CACHE_KEY);
    if (!raw) return undefined;
    let userData = JSON.parse(raw);
    const canonicalUserId = getCanonicalUserId();
    if (userData && canonicalUserId) {
      userData = { ...userData, user_id: canonicalUserId };
    }
    return userData;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[auth] Failed to read cached user metadata', error);
    return undefined;
  }
};

//get headers - no Authorization header is needed; backend uses cookies
export const getHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  return headers;
};

// userDetails function
export const getUserDetails = () => {
  const {
    user_id: userId,
    user_email: userEmail,
    first_name: firstName,
    last_name: lastName,
    pending_application_id: pendingApplicationId,
    permissions: permissions,
  } = CheckAuth() || {};

  const canonicalUserId = getCanonicalUserId();
  const effectiveUserId = canonicalUserId ?? userId;

  return {
    userId: effectiveUserId,
    userEmail,
    firstName,
    lastName,
    pendingApplicationId,
    token: null,
    permissions,
  };
};
